import { memo, useState } from "react";
import { Clock, CheckCircle2, GripVertical, ArrowUpDown, Repeat, Star } from "lucide-react";

/**
 * AppuntamentoCard - Sistema SEMPLICE:
 * - TRASCINA la card → cambia OPERATORE (con conferma)
 * - Doppio click → modifica rapida
 * - Icona freccia in alto → trascina per cambiare ORARIO
 *
 * NUOVO MODELLO: la card può rappresentare UN SINGOLO SEGMENTO DI LAVORO
 * (uno per servizio). Le pose sono renderizzate come slot viola separati
 * tra i segmenti. Il PRIMO segmento mostra l'UI completa (nome cliente,
 * orario, prezzo, drag verticale); i segmenti successivi mostrano una
 * UI compatta (solo nome del servizio).
 */
const AppuntamentoCard = memo(function AppuntamentoCard({
  app,
  operatore,
  height,
  top,
  vistaCompatta,
  incassato,
  isSlotPosa,
  slotPosaColor,
  isExternal,
  draggingApp,
  resizingApp,
  verticalDragApp,
  consulenzeStatus,
  overlappingPosa,
  // NUOVI PROPS per segmenti sequenziali
  segmento,
  isPrimoSegmento = true,
  isUltimoSegmento = true,
  totaleSegmenti = 1,
  // NUOVI PROPS per gestione lane (side-by-side quando ci sono appuntamenti sovrapposti nella stessa colonna operatore)
  laneIndex = 0,
  laneCount = 1,
  onDragStart,
  onDoubleClick,
  onConsulenzaClick,
  onSlotPosaClick,
  onResizeStart,
  onVerticalDragStart,
  onToggleFissato,
  onClienteClick,
  SLOT_HEIGHT,
  minutiAssolutoToOra
}) {
  const hasOverlap = overlappingPosa !== null && overlappingPosa !== undefined;
  const hasLaneOverlap = laneCount > 1;
  const isSegmento = !!segmento;
  const isContinuation = isSegmento && !isPrimoSegmento;
  // Quando l'utente passa sopra al bottone del nome cliente, disabilita il drag del parent
  // così il click va al button senza essere assorbito dal drag handler HTML5.
  const [hoveringClienteName, setHoveringClienteName] = useState(false);

  // Calcolo posa totale (badge informativo, visibile solo sul primo segmento)
  const tempoPosaTotale = (app.servizi || []).reduce((acc, s) => acc + (s.tempo_posa || 0), 0);

  // Servizio di riferimento per questo blocco:
  // - se è un segmento -> servizio del segmento
  // - altrimenti (compatibilità) -> primo servizio
  const servizioRif = isSegmento ? segmento.servizio : (app.servizi?.[0]);
  const coloreAppuntamento = servizioRif?.colore || operatore.colore;

  const getBackgroundStyle = () => {
    if (incassato) return undefined;
    if (isSlotPosa) return slotPosaColor;
    if (isExternal) return undefined;
    return coloreAppuntamento;
  };

  // Handler per drag ORIZZONTALE (cambio operatore) - HTML5 drag standard
  const handleDragStart = (e) => {
    if (incassato || isExternal) {
      e.preventDefault();
      return;
    }
    // Se l'utente sta cliccando sul nome del cliente, NON iniziare un drag
    if (e.target?.closest?.('[data-cliente-link="true"]')) {
      e.preventDefault();
      return;
    }
    onDragStart(e, app);
  };

  // Orario da mostrare in alto a destra
  const oraDisplay = isSegmento ? segmento.oraInizio : app.ora?.slice(0, 5);

  // Nome servizio per il segmento corrente
  const nomeServizio = servizioRif?.nome || (app.servizi?.length > 0 ? 'Servizio' : '');

  // Etichetta visiva: "1/3", "2/3"... solo se ci sono più segmenti
  const stepLabel = totaleSegmenti > 1 ? `${(segmento?.servizioIndex ?? 0) + 1}/${totaleSegmenti}` : null;

  return (
    <div
      data-testid={`appuntamento-card-${app.id}${isSegmento ? `-seg-${segmento.servizioIndex}` : ''}`}
      data-appuntamento-id={app.id}
      draggable={!incassato && !isExternal && !hoveringClienteName}
      onDragStart={handleDragStart}
      className={`absolute ${(hasOverlap || hasLaneOverlap) ? '' : 'left-0.5 right-0.5'} ${incassato ? 'bg-slate-300 text-slate-600' : isExternal ? 'bg-slate-400 text-white' : isSlotPosa ? 'text-white' : 'text-white'} ${isContinuation ? 'rounded-md' : 'rounded-lg'} shadow-md overflow-hidden group
        ${!incassato && !isExternal ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'}
        ${draggingApp?.id === app.id ? 'opacity-50' : ''}
        ${resizingApp?.id === app.id ? 'ring-2 ring-white ring-offset-1 z-30' : 'z-10 hover:z-20 hover:shadow-lg'}
        ${hasOverlap ? 'ring-1 ring-violet-300' : ''}
        ${hasLaneOverlap ? 'ring-1 ring-orange-300' : ''}
        border-2 ${isSlotPosa ? 'border-amber-300' : incassato ? 'border-green-400' : 'border-white/40'}
        ${isContinuation ? '' : `border-l-4 ${isSlotPosa ? 'border-l-amber-600' : incassato ? 'border-l-green-600' : 'border-l-white/80'}`}
      `}
      style={{
        top: `${top}px`,
        height: `${Math.max(height, SLOT_HEIGHT)}px`,
        minHeight: `${SLOT_HEIGHT}px`,
        // Priorità: lane overlap (multipli appuntamenti nella stessa fascia) > posa overlap > default full width
        ...(hasLaneOverlap ? {
          width: `calc(${100 / laneCount}% - 4px)`,
          left: `calc(${(100 / laneCount) * laneIndex}% + 2px)`,
        } : hasOverlap ? {
          width: '48%',
          right: '2px',
        } : {
          left: '2px',
        }),
        background: getBackgroundStyle(),
        boxShadow: '0 2px 4px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.2)'
      }}
      onDoubleClick={(e) => onDoubleClick(e, app)}
      title={isSegmento ? `${app.cliente_nome} • ${nomeServizio} (${segmento.oraInizio}-${segmento.oraFine})` : undefined}
    >
      {/* Barra superiore bianca */}
      {!incassato && !isExternal && (
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-white/40 via-white/60 to-white/40 rounded-t-lg" />
      )}

      {/* Badge slot posa */}
      {isSlotPosa && !incassato && height >= 18 && (
        <div className="absolute top-0 right-0 bg-amber-600/90 rounded-bl">
          <span className="text-[5px] font-bold text-white px-0.5">P</span>
        </div>
      )}

      {/* Badge incassato */}
      {incassato && (
        <div className="absolute top-0 right-0 bg-green-500 rounded-bl">
          <CheckCircle2 className="w-2 h-2 text-white" />
        </div>
      )}

      {/* === SEGMENTO PRIMARIO (o card legacy senza segmento): UI completa === */}
      {!isContinuation && (
        <div className={`px-1 py-0.5 h-full flex flex-col justify-start overflow-hidden relative z-10`}>
          {/* Nome cliente - cliccabile per aprire profilo cliente */}
          <div className="flex items-start justify-between gap-1 mb-0.5">
            {!incassato && !isExternal && onClienteClick && app.cliente_id ? (
              <button
                type="button"
                data-cliente-link="true"
                className={`bg-black/40 backdrop-blur-sm px-1.5 py-0.5 rounded-md flex-1 min-w-0 cursor-pointer hover:bg-black/70 active:bg-black/80 transition text-left relative z-30 ${height < 20 ? 'max-h-6' : ''}`}
                onMouseEnter={() => setHoveringClienteName(true)}
                onMouseLeave={() => setHoveringClienteName(false)}
                onMouseDown={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                onMouseUp={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setHoveringClienteName(false);
                  onClienteClick(app);
                }}
                onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
                draggable={false}
                title={app.cliente_nome || 'Cliente'}
                data-testid={`appuntamento-cliente-link-${app.id}`}
              >
                <p
                  className={`font-black text-white leading-tight break-words line-clamp-2 ${
                    height < 25 ? 'text-[10px]' :
                    height < 40 ? 'text-[11px]' :
                    vistaCompatta ? 'text-xs' : 'text-sm'
                  }`}
                  style={{ textShadow: '1px 1px 1px rgba(0,0,0,0.8)' }}
                >
                  {app.cliente_nome || 'Cliente'}
                </p>
              </button>
            ) : (
              <div
                className={`bg-black/40 backdrop-blur-sm px-1.5 py-0.5 rounded-md flex-1 min-w-0 ${height < 20 ? 'max-h-6' : ''}`}
              >
                <p
                  className={`font-black text-white leading-tight break-words line-clamp-2 ${
                    height < 25 ? 'text-[10px]' :
                    height < 40 ? 'text-[11px]' :
                    vistaCompatta ? 'text-xs' : 'text-sm'
                  } ${incassato ? 'line-through opacity-70' : ''}`}
                  style={{ textShadow: '1px 1px 1px rgba(0,0,0,0.8)' }}
                  title={app.cliente_nome || 'Cliente'}
                >
                  {app.cliente_nome || 'Cliente'}
                </p>
              </div>
            )}
            <div className="flex items-center gap-0.5 shrink-0">
              {app.cliente_nuovo && (
                <Star className={`text-amber-300 fill-amber-300 ${height < 30 ? 'w-2.5 h-2.5' : 'w-3 h-3'}`} title="Cliente Nuovo" data-testid="cliente-nuovo-badge" />
              )}
              {app.ricorrente && (
                <Repeat className={`text-white/90 ${height < 30 ? 'w-2.5 h-2.5' : 'w-3 h-3'}`} title="Ricorrente" data-testid="recurring-badge" />
              )}
              <span className={`font-bold bg-black/50 px-1.5 py-0.5 rounded-md text-white ${height < 30 ? 'text-[8px]' : 'text-[10px]'}`}>
                {oraDisplay}
              </span>
            </div>
          </div>

          {/* Servizio del segmento corrente (o singolo) */}
          <div className={`flex items-center gap-1 mt-0.5 ${height < 30 ? 'text-[7px]' : vistaCompatta ? 'text-[8px]' : 'text-[9px]'}`}>
            {servizioRif?.colore && (
              <span
                className="w-2 h-2 rounded-full shrink-0 border border-white/50"
                style={{ backgroundColor: servizioRif.colore }}
              />
            )}
            <p className={`font-semibold truncate leading-none bg-black/15 px-0.5 rounded flex-1 ${incassato ? 'opacity-50 line-through' : ''}`}>
              {nomeServizio}
            </p>
            {stepLabel && (
              <span className="bg-white/30 text-white font-bold rounded px-1 text-[8px] shrink-0">{stepLabel}</span>
            )}
          </div>

          {/* Warning Consulenza */}
          {!consulenzeStatus[app.cliente_id] && !incassato && height >= 40 && (
            <button
              onClick={(e) => onConsulenzaClick(app.cliente_id, app.cliente_nome, e)}
              className="inline-flex items-center bg-red-500 hover:bg-red-600 text-white rounded font-bold cursor-pointer self-start mt-0.5 px-0.5 text-[6px]"
              title="Consulenza mancante"
              data-testid="consulenza-badge"
            >
              ⚠️
            </button>
          )}

          {/* Durata e prezzo - mostrati in fondo */}
          {height >= 55 && (
            <div className={`flex justify-between items-center mt-auto pt-0.5 ${height < 40 ? 'text-[6px]' : 'text-[8px]'}`}>
              <span className="bg-black/20 px-1 rounded">{app.durata_totale || 30}&apos;</span>
              <span className="font-bold bg-white/30 px-1 rounded">€{app.totale}</span>
            </div>
          )}
        </div>
      )}

      {/* === SEGMENTO DI CONTINUAZIONE: UI minimale (solo servizio) === */}
      {isContinuation && (
        <div className="px-1 py-0.5 h-full flex flex-col justify-center items-center overflow-hidden relative z-10">
          <div className="flex items-center gap-1 max-w-full">
            {servizioRif?.colore && (
              <span
                className="w-2 h-2 rounded-full shrink-0 border border-white/50"
                style={{ backgroundColor: servizioRif.colore }}
              />
            )}
            <span
              className={`font-bold text-white truncate bg-black/30 px-1 rounded ${
                height < 22 ? 'text-[8px]' : height < 35 ? 'text-[10px]' : 'text-xs'
              }`}
              style={{ textShadow: '1px 1px 1px rgba(0,0,0,0.8)' }}
            >
              {nomeServizio}
            </span>
            {stepLabel && (
              <span className="bg-white/40 text-white font-bold rounded px-1 text-[8px] shrink-0">{stepLabel}</span>
            )}
          </div>
          {height >= 30 && (
            <span className="text-[8px] font-semibold bg-black/30 text-white px-1 rounded mt-0.5">
              {segmento.oraInizio}
            </span>
          )}
        </div>
      )}

      {/* Indicatore Posa - solo sul PRIMO segmento */}
      {tempoPosaTotale > 0 && !incassato && !isContinuation && (
        <div
          className="absolute bottom-0.5 right-0.5 bg-amber-500/90 text-white px-1 py-0.5 rounded text-[6px] font-bold flex items-center gap-0.5"
          title={`Tempo posa totale: ${tempoPosaTotale} min`}
        >
          <Clock className="w-2 h-2" />
          {tempoPosaTotale}&apos;
        </div>
      )}

      {/* Vertical Drag Handle: piccola icona in alto a destra (non copre il nome) */}
      {!incassato && !isExternal && height >= 35 && !isContinuation && (
        <div
          className="absolute top-0 right-0 w-4 h-4 cursor-ns-resize flex items-center justify-center opacity-40 hover:opacity-100 bg-black/40 rounded-bl-lg transition-opacity z-20"
          onMouseDown={(e) => onVerticalDragStart(e, app)}
          title="Trascina per cambiare orario"
        >
          <ArrowUpDown className="w-2.5 h-2.5 text-white" />
        </div>
      )}

      {/* Resize handle: solo sull'ULTIMO segmento */}
      {!incassato && !isExternal && isUltimoSegmento && (
        <div
          className="absolute bottom-0 left-0 right-0 h-3 cursor-ns-resize flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black/20"
          onMouseDown={(e) => onResizeStart(e, app)}
          title="Trascina per cambiare durata"
        >
          <GripVertical className="w-4 h-4 rotate-90" />
        </div>
      )}
    </div>
  );
});

export default AppuntamentoCard;
